<template>
  <div class="mini-game-levels-page">
    <div class="page-header">
      <div class="left">
        <el-button icon="el-icon-back" circle @click="$router.push('/mini-games')"></el-button>
        <h2 v-if="miniGame">{{ miniGame.name }} - 关卡列表</h2>
      </div>
      <div class="page-actions">
        <el-button type="primary" @click="handleCreate">添加关卡</el-button>
      </div>
    </div>

    <div class="levels-container" v-loading="loading">
      <el-table
        :data="levels"
        border
        style="width: 100%"
        row-key="id"
      >
        <el-table-column width="60" align="center">
          <template slot="header">
            <i class="el-icon-rank"></i>
          </template>
          <template>
            <i class="el-icon-rank drag-handler" style="cursor: move; color: #909399"></i>
          </template>
        </el-table-column>
        <el-table-column prop="id" label="ID" width="80" align="center" />
        <el-table-column v-if="miniGame && miniGame.game_type !== 'drive_truck'" label="地图" width="120" align="center">
          <template slot-scope="scope">
            <el-image
              v-if="scope.row.map_image"
              :src="resolveMediaUrl(scope.row.map_image)"
              :preview-src-list="[resolveMediaUrl(scope.row.map_image)]"
              class="map-preview"
            />
            <span v-else style="color: #c0c4cc">未设置</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="200" show-overflow-tooltip />
        <el-table-column label="通关条件" min-width="250">
          <template slot-scope="scope">
            <div v-if="scope.row.config && scope.row.config.clearConditions && scope.row.config.clearConditions.length > 0">
              <el-tag
                v-for="(cond, idx) in scope.row.config.clearConditions"
                :key="idx"
                size="mini"
                style="margin: 2px 4px 2px 0;"
                effect="plain"
              >
                {{ cond.description || '未命名' }}: 收集{{ cond.collectibleItemType }} ×{{ cond.requiredCount }}
              </el-tag>
            </div>
            <span v-else style="color: #c0c4cc; font-size: 12px;">无</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" align="center">
          <template slot-scope="scope">
            <el-button type="text" @click="handleEditInfo(scope.row)">修改信息</el-button>
            <el-button type="text" @click="handleEditCanvas(scope.row)">编辑关卡</el-button>
            <el-button type="text" style="color: #F56C6C" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Basic Info Dialog -->
    <el-dialog
      :title="infoTitle"
      :visible.sync="infoVisible"
      width="500px"
    >
      <el-form label-width="80px">
        <el-form-item label="备注">
          <el-input v-model="currentLevel.remark" placeholder="请输入关卡备注" />
        </el-form-item>
        <el-form-item v-if="miniGame && miniGame.game_type !== 'drive_truck'" label="地图图片">
          <div style="display: flex; gap: 10px; align-items: center;">
            <el-input v-model="currentLevel.map_image" placeholder="图片路径" />
            <el-upload
              action="/api/upload/mini-game-map"
              :show-file-list="false"
              :on-success="handleMapUploadSuccess"
              :headers="uploadHeaders"
              name="file"
            >
              <el-button size="small" type="primary">上传</el-button>
            </el-upload>
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="infoVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveInfo">确定</el-button>
      </div>
    </el-dialog>

    <!-- Canvas Editor Dialog -->
    <el-dialog
      v-if="editorVisible"
      title="关卡配置编辑"
      :visible.sync="editorVisible"
      fullscreen
      custom-class="full-editor-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <mini-game-level-editor
        :initial-config="currentLevel.config"
        :initial-assets="currentLevel.assets"
        :map-url="currentLevel.map_image"
        :game-type="miniGame ? miniGame.game_type : ''"
        @save="handleSaveLevel"
        @cancel="editorVisible = false"
      />
    </el-dialog>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import Sortable from "sortablejs";
import MiniGameLevelEditor from "@/components/MiniGameLevelEditor.vue";
import { getMiniGame, getMiniGameLevels, createMiniGameLevel, updateMiniGameLevel, deleteMiniGameLevel, reorderMiniGameLevels } from "@/net/api";
import { resolveMediaUrl } from "@/utils/media";

export default {
  name: "MiniGameLevels",
  components: { MiniGameLevelEditor, draggable },
  data() {
    return {
      gameId: null,
      miniGame: null,
      levels: [],
      loading: false,
      editorVisible: false,
      infoVisible: false,
      infoTitle: "添加关卡",
      currentLevel: { id: null, remark: "", map_image: "", config: {}, assets: {} }
    };
  },
  computed: {
    uploadHeaders() {
      const token = localStorage.getItem("token");
      return token ? { Authorization: `Bearer ${token}` } : {};
    }
  },
  created() {
    this.gameId = this.$route.params.id;
    this.loadData();
  },
  mounted() {
    this.initSortable();
  },
  methods: {
    resolveMediaUrl,
    async loadData() {
      this.loading = true;
      try {
        const [gameRes, levelsRes] = await Promise.all([
          getMiniGame(this.gameId),
          getMiniGameLevels(this.gameId)
        ]);
        this.miniGame = gameRes.data;
        this.levels = levelsRes.data || [];
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message 
          ? e.response.data.message 
          : "加载数据失败";
        this.$message.error(msg);
      } finally {
        this.loading = false;
      }
    },
    initSortable() {
      this.$nextTick(() => {
        const el = document.querySelector('.el-table__body-wrapper tbody');
        if (!el) return;
        this.sortable = Sortable.create(el, {
          handle: '.drag-handler',
          onEnd: evt => {
            const { oldIndex, newIndex } = evt;
            if (oldIndex === newIndex) return;
            
            const currRow = this.levels.splice(oldIndex, 1)[0];
            this.levels.splice(newIndex, 0, currRow);
            
            this.handleReorder();
          }
        });
      });
    },
    async handleReorder() {
      try {
        await reorderMiniGameLevels(this.gameId, { level_ids: this.levels.map(l => l.id) });
        this.$message.success("排序已保存");
      } catch (e) {
        this.$message.error("排序保存失败");
        this.loadData();
      }
    },
    handleCreate() {
      this.currentLevel = { id: null, remark: "", map_image: "", config: {}, assets: {} };
      this.infoTitle = "添加关卡";
      this.infoVisible = true;
    },
    handleEditInfo(row) {
      this.currentLevel = JSON.parse(JSON.stringify(row));
      this.infoTitle = "修改信息";
      this.infoVisible = true;
    },
    handleEditCanvas(row) {
      this.currentLevel = JSON.parse(JSON.stringify(row));
      this.editorVisible = true;
    },
    handleMapUploadSuccess(res) {
      this.currentLevel.map_image = res.url;
      this.$message.success("地图上传成功");
    },
    async handleSaveInfo() {
      if (this.miniGame && this.miniGame.game_type !== 'drive_truck' && !this.currentLevel.map_image) {
        return this.$message.warning("请上传或输入地图图片路径");
      }
      const payload = {
        remark: this.currentLevel.remark,
        map_image: this.currentLevel.map_image || "",
        config: this.currentLevel.config,
        assets: this.currentLevel.assets
      };
      try {
        if (this.currentLevel.id) {
          await updateMiniGameLevel(this.currentLevel.id, payload);
        } else {
          await createMiniGameLevel(this.gameId, payload);
        }
        this.$message.success("保存成功");
        this.infoVisible = false;
        this.loadData();
      } catch (e) {
        this.$message.error("保存失败");
      }
    },
    async handleSaveLevel({ config, assets, map_image, config_url }) {
      const payload = {
        remark: this.currentLevel.remark,
        map_image: map_image || this.currentLevel.map_image,
        config: config,
        assets: assets,
        config_url: config_url
      };
      
      try {
        if (this.currentLevel.id) {
          await updateMiniGameLevel(this.currentLevel.id, payload);
        } else {
          await createMiniGameLevel(this.gameId, payload);
        }
        this.editorVisible = false;
        this.loadData();
        // 延迟一点显示成功，避免和关闭动画冲突
        setTimeout(() => {
          this.$message.success("关卡保存成功");
        }, 300);
      } catch (e) {
        this.$message.error("保存数据库失败");
      }
    },
    async handleDelete(row) {
      try {
        await this.$confirm("确定删除该关卡吗？", "提示", { type: "warning" });
        await deleteMiniGameLevel(row.id);
        this.$message.success("删除成功");
        this.loadData();
      } catch (e) {
        if (e !== "cancel") this.$message.error("删除失败");
      }
    }
  }
};
</script>

<style scoped>
.mini-game-levels-page {
  padding: 20px;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.page-header .left {
  display: flex;
  align-items: center;
  gap: 15px;
}
.page-header h2 {
  margin: 0;
}
.map-preview {
  width: 80px;
  height: 45px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
}
.full-editor-dialog {
  display: flex;
  flex-direction: column;
}
.full-editor-dialog >>> .el-dialog__header {
  display: none;
}
.full-editor-dialog >>> .el-dialog__body {
  padding: 0;
  flex: 1;
  overflow: hidden;
}
</style>

<style>
.full-editor-dialog .el-dialog__body {
  padding: 0;
  height: calc(100vh - 54px);
  display: flex;
  flex-direction: column;
}
.full-editor-dialog .el-dialog__header {
  padding: 15px 20px;
}
.full-editor-dialog .editor-remark-form {
  padding: 10px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
}
</style>
